import { WifiOff } from 'lucide-react'
import './errors.css'
import { Button } from '../../platform/primitives/Button'

/**
 * "You're offline" — the design's `is.offline` full-screen state, transcribed
 * from the comp's structure and spacing (rule 2: never its inline styles).
 * Full-viewport centring and its own `<h1>`, the same shape `SessionExpired`
 * and `SessionUnavailable` use rather than `NotFound`'s: those two are given
 * by their caller in place of the whole chrome subtree, with no `ChromeLayout`
 * above them to own the heading or the centring, and connectivity is exactly
 * that kind of app-wide condition rather than one route's content. Wiring
 * this in — where in the tree `useOnline()` gets read to decide when this
 * mounts — is not this file's to do; see the CSS list below for what that
 * caller still owes it.
 *
 * **Half the comp describes a mutation queue this app doesn't have.** The
 * source draws a panel — "2 category changes waiting to sync", "1 split not
 * yet saved", "Last full sync 11 minutes ago" — and a body sentence promising
 * queued edits will sync once back online. `grep -rn
 * "persistQueryClient|onlineManager" src/` returns nothing in application code. TanStack Query here holds
 * server state in memory for the session, and no mutation is persisted or replayed after a reload. The
 * transaction autosave sets `networkMode: 'always'` on its PATCH and its re-read, so offline it is attempted
 * and fails at once. Other mutations keep the library default, which PAUSES one offline and resumes it
 * on reconnect, in memory only; no surface promises that, and none is built on it. Every number in that panel would
 * be invented, and the queued-edits sentence is a promise this app would
 * break — a transaction edit attempted offline fails outright, it is not held. Both are
 * dropped rather than built to match a mechanism that isn't there.
 *
 * **The note that stays is narrower than the comp's, on purpose.** "You can
 * still read everything that was loaded" is true only of what is already
 * sitting in the in-memory query cache, and only until the tab reloads —
 * nothing here is persisted. The sentence below says that instead of the
 * broader claim.
 *
 * **Retry reloads the page**, not `go.overview` — the comp's click target,
 * which assumes edits are waiting to flush. There is nothing to flush, so the
 * one honest action is re-checking connectivity and re-fetching everything
 * fresh, which a reload does directly. Same default `ErrorBoundary` already
 * uses for its own "Reload the page" recovery, and `onRetry` is injectable
 * for the same reason `onReload` is there: a real reload is not something a
 * test can observe finishing.
 */
export function Offline({ onRetry = () => window.location.reload() }: { onRetry?: () => void }) {
  return (
    <div className="offline-page">
      <div className="offline-panel">
        <span className="offline-icon">
          <WifiOff size={26} strokeWidth={1.8} aria-hidden="true" />
        </span>
        <div className="offline-text">
          <h1 className="offline-title">You&apos;re offline</h1>
          <p className="offline-note">
            What&apos;s already loaded in this tab is still here to read — reloading, or opening
            something new, needs the connection back.
          </p>
        </div>
        <Button variant="primary" size="form" onClick={onRetry}>
          Retry now
        </Button>
      </div>
    </div>
  )
}
